// Карточка «Сейчас открыто»: открытые заказы с позициями кухни (iikoCloud, обновляется раз в минуту)
import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { CARD_LABEL_COLOR, CARD_SURFACE_SX, MUTED_TEXT_COLOR, REDUCED_MOTION, SECTION_ACCENTS } from '../../../styles/tokens';
import { STALE_COLOR, clockTime, formatDuration, formatMoney, itemsText, orderTitle, ordersText } from '../kitchenFormat';

const ACCENT = SECTION_ACCENTS['/kitchen'];

// «Живой» индикатор: гаснет и разгорается (только opacity)
const breathe = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;


// Открытые заказы для выбора не настроены — что сделать администратору
function unavailableText(reason, selection) {
  if (reason === 'restaurant_not_connected') {
    return `Ресторан «${selection}» не подключён к iikoCloud — администратор выбирает организации в настройках iikoCloud.`;
  }
  if (reason === 'no_organizations') {
    return 'Организации iikoCloud не выбраны — администратор выбирает их в настройках iikoCloud.';
  }
  return `Для группы «${selection}» открытые заказы не настроены — администратор сопоставляет группы в настройках iikoCloud.`;
}

export function liveUnavailableText(lastUpdated) {
  return lastUpdated ? `Живые данные недоступны с ${clockTime(lastUpdated)}` : 'Живые данные сейчас недоступны';
}

export default function LiveCard({ live, failed, lastUpdated, group }) {
  const liveFresh = !failed && Boolean(live?.available) && !live.stale;
  let body;
  if (failed) {
    body = <Typography sx={{ color: CARD_LABEL_COLOR }}>{liveUnavailableText(lastUpdated)}</Typography>;
  } else if (!live) {
    body = <Skeleton variant="text" width="60%" height={40} />;
  } else if (!live.available) {
    body = <Typography sx={{ color: CARD_LABEL_COLOR }}>{unavailableText(live.reason, group)}</Typography>;
  } else {
    const { totals, oldest } = live;
    body = (
      <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1.5 }}>
          <Typography sx={{ fontSize: { xs: '26px', md: '28px' }, fontWeight: 800, color: '#fff' }}>{ordersText(totals.orders)}</Typography>
          <Typography sx={{ color: CARD_LABEL_COLOR }}>
            {itemsText(totals.items)} · {formatMoney(totals.sum)}
          </Typography>
        </Box>
        {oldest && (
          <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
            Самый давний: {orderTitle(oldest, { lower: true })}, открыт {formatDuration(oldest.minutes_open)}
          </Typography>
        )}
        {/* Последний удачный ответ: в итоги отчёта открытые сейчас не добавляются (заказ мог уже закрыться) */}
        {/* «Спящие» группы терминалов: касса не на связи — её заказы могут быть не все */}
        {live.offline_terminals?.length > 0 && (
          <Typography role="status" sx={{ fontSize: '13px', color: STALE_COLOR }}>
            {live.offline_terminals.length === 1
              ? `Касса «${live.offline_terminals[0]}» не на связи, данные неполные`
              : `Кассы ${live.offline_terminals.map((name) => `«${name}»`).join(', ')} не на связи, данные неполные`}
          </Typography>
        )}
        {live.stale && (
          <Typography role="status" sx={{ fontSize: '13px', color: STALE_COLOR }}>
            данные на {clockTime(live.updated_at)}, iikoCloud не отвечает
          </Typography>
        )}
      </>
    );
  }

  return (
    <Box
      component="section"
      aria-label="Сейчас открыто"
      sx={{
        ...CARD_SURFACE_SX,
        border: `1px solid rgba(${ACCENT.rgb},0.6)`,
        width: '100%',
        boxSizing: 'border-box',
        p: { xs: 2, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '13px', fontWeight: 600, color: ACCENT.tab }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: ACCENT.accent,
            // Живые данные свежие — индикатор плавно «дышит»; нет ответа или данные устарели — горит ровно
            ...(liveFresh && { animation: `${breathe} 2.4s ease-in-out infinite` }),
            [REDUCED_MOTION]: { animation: 'none' },
          }}
        />
        Сейчас открыто
        {live?.available && (
          <Typography component="span" sx={{ ml: 'auto', fontSize: '12px', fontWeight: 400, color: MUTED_TEXT_COLOR }}>
            {live.stale ? '' : `обновлено ${clockTime(live.updated_at)}`}
            {live.complete === false ? ' · данные могут быть неполными' : ''}
          </Typography>
        )}
      </Box>
      {body}
    </Box>
  );
}
